import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useDropzone } from 'react-dropzone';
import dayjs from '~/timeInit';

import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useUploadCostPrices } from '~/hooks/apis/tours/useTourCostPrices';
import { useGetTourDepartures } from '~/hooks/apis/tours/useTourDepartures';

import { SlidingScaleByDeparturePricingItem, TourService } from '~/types/tours/costPrice';

import UploadPreviewTable from './UploadPreviewTable';
import {
  departureSelectorKey,
  groupDeparturesForSelector,
  mergePricingItems,
  resolveUploadedDepartures,
  summariseMerge,
} from './departureRows';
import {
  ParsedSlidingScaleRow,
  buildPricingCsv,
  buildPricingFilename,
  buildTemplateCsv,
  buildTemplateFilename,
  downloadCsv,
  parseSlidingScaleCsv,
} from './slidingScaleCsv';

/** Enough of the sheet to check it parsed as expected, without listing it all. */
const MAX_PREVIEW_ROWS = 20;

interface Props {
  open: boolean;
  service: TourService;
  /** How many sliding scale rows the service already has, if any. */
  existingRowCount?: number;
  /**
   * Prices this service keys by departure, so the sheet carries a Departure
   * column and the upload is sent as the by-departure kind.
   */
  byDeparture?: boolean;
  /** The tour being priced, so its departures can be offered to choose from. */
  tourId?: string;
  /** Departures to pre-fill the template with, one row per package option. */
  departures?: Array<string>;
  /**
   * What the service already holds. An upload replaces a package option's
   * stored pricing rather than merging into it, so a sheet covering one
   * departure is folded into these and the union is sent.
   */
  existingItems?: Array<SlidingScaleByDeparturePricingItem>;
  onClose: () => void;
}

export default function SlidingScaleUploadDialog({
  open,
  service,
  existingRowCount = 0,
  byDeparture = false,
  tourId,
  departures,
  existingItems = [],
  onClose,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [fileName, setFileName] = useState<string | null>(null);
  const [parseErrors, setParseErrors] = useState<Array<string>>([]);
  const [rows, setRows] = useState<Array<ParsedSlidingScaleRow>>([]);

  const uploadCostPrices = useUploadCostPrices({ supplierId: service.supplierId, serviceId: service.id });

  const { data: tourDepartures = [], isLoading: isLoadingDepartures } = useGetTourDepartures(
    byDeparture ? tourId : undefined,
  );

  /**
   * The departures one sheet prices, by id, which is what cost prices are keyed
   * by. Most departures share a scale, so choosing several (or all) prices them
   * with one sheet and no Departure column. Editing one departure arrives with
   * it already chosen.
   */
  const [selectedDepartures, setSelectedDepartures] = useState<Array<string>>(departures ?? []);

  const scope = useMemo(
    () => ({
      byDeparture,
      departures: selectedDepartures.length ? selectedDepartures : departures,
      defaultDepartures: selectedDepartures.length ? selectedDepartures : undefined,
    }),
    [byDeparture, departures, selectedDepartures],
  );

  const reset = useCallback(() => {
    setFileName(null);
    setParseErrors([]);
    setRows([]);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const onDrop = useCallback(
    (files: Array<File>) => {
      const file = files[0];
      if (!file) {
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const parsed = parseSlidingScaleCsv(String(reader.result ?? ''), scope);
        setFileName(file.name);
        setParseErrors(parsed.errors);
        setRows(parsed.rows);
      };
      reader.onerror = () => {
        setParseErrors(['Could not read the selected file']);
      };
      reader.readAsText(file);
    },
    [scope],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
    },
  });

  /**
   * svc-tour takes the whole sheet in one request and resolves package names
   * against the service's configurations, so rows are sent together rather than
   * one request per row.
   */
  /**
   * Sheets name departures because nobody types a uuid, but prices are keyed by
   * id, so every row is resolved before anything is sent.
   */
  const { uploadedItems, resolutionErrors } = useMemo(() => {
    const uploadedRows = rows as Array<{ item: SlidingScaleByDeparturePricingItem; rowNumber: number }>;

    if (!byDeparture) {
      return { uploadedItems: uploadedRows.map(({ item }) => item), resolutionErrors: [] as Array<string> };
    }

    // A row naming no departure is the scale for every departure chosen above.
    const expandedRows = uploadedRows.flatMap((row) =>
      row.item.departure
        ? [row]
        : selectedDepartures.map((departure) => ({ ...row, item: { ...row.item, departure } })),
    );

    const { items, errors } = resolveUploadedDepartures(tourDepartures, expandedRows);

    return { uploadedItems: items, resolutionErrors: errors };
  }, [byDeparture, rows, selectedDepartures, tourDepartures]);

  /** The union actually sent, so departures the sheet omits survive. */
  const mergedPricing = useMemo(() => mergePricingItems(existingItems, uploadedItems), [existingItems, uploadedItems]);

  const mergeSummary = useMemo(() => summariseMerge(existingItems, uploadedItems), [existingItems, uploadedItems]);

  const handleUpload = useCallback(async () => {
    if (!rows.length) {
      return;
    }

    try {
      await uploadCostPrices.mutateAsync(
        byDeparture
          ? { kind: 'SlidingScaleByDeparture', pricing: mergedPricing }
          : { kind: 'SlidingScale', pricing: rows.map((row) => row.item) },
      );

      enqueueSnackbar(
        byDeparture && selectedDepartures.length > 1
          ? `Uploaded ${rows.length} cost price row${rows.length > 1 ? 's' : ''} to ${selectedDepartures.length} departures`
          : `Uploaded ${rows.length} cost price row${rows.length > 1 ? 's' : ''}`,
        { variant: 'success' },
      );
      handleClose();
    } catch (error) {
      enqueueSnackbar(`Upload failed: ${error?.message ?? 'Unknown error'}`, {
        variant: 'error',
        autoHideDuration: 10000,
      });
    }
  }, [byDeparture, enqueueSnackbar, handleClose, mergedPricing, rows, selectedDepartures, uploadCostPrices]);

  /**
   * Editing one departure downloads just that departure's rates, so the sheet
   * is what was clicked rather than the whole service.
   */
  const downloadItems = useMemo(() => {
    if (selectedDepartures.length === 1) {
      return existingItems.filter((item) => item.departure === selectedDepartures[0]);
    }
    // Several departures can hold different scales, so no one sheet is theirs.
    if (selectedDepartures.length > 1) {
      return [];
    }
    return existingItems;
  }, [existingItems, selectedDepartures]);

  /**
   * A tour lists a departure once per tour option leaving that day, and the tab
   * reads a departure's prices across all of them, so the picker offers each
   * departure once and prices it against one of its ids.
   */
  const departureGroups = useMemo(
    () =>
      groupDeparturesForSelector(
        tourDepartures.slice().sort((a, b) => a.departureDate.localeCompare(b.departureDate)),
      ).map(([, group]) => group),
    [tourDepartures],
  );

  const chosenGroups = useMemo(
    () =>
      departureGroups.filter((group) => group.some((departure) => selectedDepartures.includes(departure.departureId))),
    [departureGroups, selectedDepartures],
  );

  // Keeps the id a departure was already chosen by, e.g. the row it was opened from.
  const selectGroups = useCallback(
    (groups: Array<(typeof departureGroups)[number]>) =>
      setSelectedDepartures(
        groups.map(
          (group) =>
            group.find((departure) => selectedDepartures.includes(departure.departureId))?.departureId ??
            group[0].departureId,
        ),
      ),
    [selectedDepartures],
  );

  const departureNames = useMemo(
    () =>
      new Map(
        tourDepartures.map((departure) => [departure.departureId, departure.departureName ?? departure.departureId]),
      ),
    [tourDepartures],
  );

  // Names rather than ids, and a count once the list stops being readable.
  const describeDepartures = useCallback(
    (ids: Array<string>): string => {
      const names = ids.map((id) => departureNames.get(id) ?? id);
      return names.length > 3 ? `${names.length} departures` : names.join(', ');
    },
    [departureNames],
  );

  const allErrors = useMemo(() => parseErrors.concat(resolutionErrors), [parseErrors, resolutionErrors]);

  // Uploading the rows that did parse would save a partial sheet as if it were whole.
  const canUpload = !!rows.length && !allErrors.length;

  // Choosing a departure is a step only a by-departure service has.
  const steps = byDeparture
    ? { departure: '1.', sheet: '2.', upload: '3.' }
    : { departure: '', sheet: '1.', upload: '2.' };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>{existingRowCount > 0 ? 'Re-upload' : 'Upload'} sliding scale cost prices</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {byDeparture && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">{steps.departure} Which departures</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!departureGroups.length || chosenGroups.length === departureGroups.length}
                  onClick={() => selectGroups(departureGroups)}
                >
                  All departures
                </Button>
                <Button size="small" disabled={!selectedDepartures.length} onClick={() => setSelectedDepartures([])}>
                  Clear
                </Button>
              </Stack>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={departureGroups}
                value={chosenGroups}
                onChange={(_event, next) => selectGroups(next)}
                getOptionLabel={([option]) =>
                  `${option.departureName ?? ''} · ${dayjs(option.departureDate).format('D MMM YYYY')}`
                }
                isOptionEqualToValue={([option], [value]) =>
                  departureSelectorKey(option) === departureSelectorKey(value)
                }
                renderOption={(props, [option]) => (
                  <Box component="li" {...props} key={departureSelectorKey(option)}>
                    <Stack>
                      <Typography variant="body2">{option.departureName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(option.departureDate).format('D MMM YYYY')}
                      </Typography>
                    </Stack>
                  </Box>
                )}
                loading={isLoadingDepartures}
                limitTags={4}
                sx={{ maxWidth: 640 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Departures"
                    helperText={
                      selectedDepartures.length
                        ? `One sheet prices all ${selectedDepartures.length} chosen departure${
                            selectedDepartures.length > 1 ? 's' : ''
                          }. The sheet needs no Departure column.`
                        : 'Choose one or more and the sheet needs no Departure column, or leave empty to name a departure on every row'
                    }
                  />
                )}
              />
            </Stack>
          )}

          <Stack spacing={1}>
            <Typography variant="subtitle2">{steps.sheet} Start from a sheet</Typography>
            <Typography variant="body2" color="text.secondary">
              {byDeparture && !selectedDepartures.length
                ? "Pre-filled with this service's packages. Name a departure on every row, and fill in a row per season."
                : "Pre-filled with this service's packages. Fill in a row per season."}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                size="small"
                onClick={() => downloadCsv(buildTemplateFilename(service, scope), buildTemplateCsv(service, scope))}
              >
                Download template
              </Button>
              {!!downloadItems.length && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    downloadCsv(buildPricingFilename(service, scope), buildPricingCsv(downloadItems, scope))
                  }
                >
                  Download current prices
                </Button>
              )}
            </Stack>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2">{steps.upload} Upload it back</Typography>
            <Box
              {...getRootProps()}
              sx={{
                p: 4,
                border: '1px dashed',
                borderColor: isDragActive ? 'primary.main' : 'divider',
                borderRadius: 1,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: isDragActive ? 'action.hover' : 'background.default',
              }}
            >
              <input {...getInputProps()} />
              <UploadFileIcon color="action" />
              <Typography variant="body2">
                {fileName ?? 'Drop a cost price CSV here, or click to choose a file'}
              </Typography>
            </Box>
          </Stack>

          {!byDeparture && existingRowCount > 0 && (
            <Alert severity="warning">
              This service already has {existingRowCount} sliding scale row{existingRowCount === 1 ? '' : 's'}.
              Uploading replaces the pricing for any package and currency in the sheet, including seasons the sheet
              leaves out.
            </Alert>
          )}

          {byDeparture && !!rows.length && (
            <Alert severity="info">
              {[
                mergeSummary.added.length ? `Adds ${describeDepartures(mergeSummary.added)}` : '',
                mergeSummary.replaced.length ? `Replaces ${describeDepartures(mergeSummary.replaced)}` : '',
                mergeSummary.untouched.length
                  ? `Leaves ${mergeSummary.untouched.length} other departure${
                      mergeSummary.untouched.length > 1 ? 's' : ''
                    } as they are`
                  : '',
              ]
                .filter(Boolean)
                .join('. ')}
              .
            </Alert>
          )}

          {uploadCostPrices.isPending && <LinearProgress />}

          {!!allErrors.length && (
            <Alert severity="error">
              <AlertTitle>Fix the file and upload it again</AlertTitle>
              <Stack component="ul" sx={{ pl: 2, m: 0 }}>
                {allErrors.slice(0, 10).map((error) => (
                  <li key={error}>
                    <Typography variant="body2">{error}</Typography>
                  </li>
                ))}
              </Stack>
              {allErrors.length > 10 && <Typography variant="body2">…and {allErrors.length - 10} more</Typography>}
            </Alert>
          )}

          <UploadPreviewTable rows={rows} limit={MAX_PREVIEW_ROWS} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleUpload} disabled={!canUpload || uploadCostPrices.isPending}>
          Upload {rows.length || ''} row{rows.length === 1 ? '' : 's'}
        </Button>
        <Button variant="text" onClick={handleClose} disabled={uploadCostPrices.isPending}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
